import { localizedStringForLocale } from "src/utils/mapEntertainment";

/** @param {unknown} value */
const sanitizeImagePath = (value) =>
  typeof value === "string" ? value.trim().replace(/^\/+/, "") : "";

/**
 * Strip ensureUniqueMediaFilename suffixes (`logo-1.png` → `logo.png`) so Hero
 * hits the real CDN object when Media.filename was uniquified for path aliases.
 * @param {string} filename
 */
const cdnBasenameFromMediaFilename = (filename) => {
  const match = filename.match(/^(.*)-(\d+)(\.[^.]+)$/);
  if (!match) return filename;
  const num = Number(match[2]);
  if (!(num > 0 && num < 1000)) return filename;
  return `${match[1]}${match[3]}`;
};

/** @param {unknown} value */
const imagePathFromUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) return "";
  const trimmed = value.trim();

  let path = "";
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const { pathname } = new URL(trimmed);
      path = pathname
        .replace(/^\/+/, "")
        .replace(/^pgfe\/img\//i, "")
        .replace(/^fit-in\/[^/]+\//i, "");
    } catch {
      return "";
    }
  } else {
    path = trimmed
      .replace(/^\/+/, "")
      .replace(/^pgfe\/img\//i, "")
      .replace(/^fit-in\/[^/]+\//i, "");
  }

  // Alias media rows may expose …/file-1.png while CDN serves …/file.png
  const parts = path.split("/");
  const last = parts[parts.length - 1];
  if (last) {
    parts[parts.length - 1] = cdnBasenameFromMediaFilename(last);
  }
  return parts.join("/");
};

/** @param {unknown} value */
const isLocaleMap = (value) =>
  Boolean(
    value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      ("en" in value || "fr" in value) &&
      !("url" in value) &&
      !("filename" in value) &&
      !("id" in value && ("mimeType" in value || "filesize" in value)),
  );

/**
 * Resolve a Payload upload field (id | media object | `{en,fr}` map) to a CDN-relative path.
 * Numeric IDs alone cannot be resolved here — live preview must hydrate them first.
 *
 * @param {unknown} upload
 * @param {string} [locale]
 * @returns {string|undefined}
 */
export const resolveHighHandMediaPath = (upload, locale = "en") => {
  if (upload == null) return undefined;

  if (isLocaleMap(upload)) {
    const picked = upload[locale] ?? upload.en ?? upload.fr;
    return resolveHighHandMediaPath(picked, locale);
  }

  if (typeof upload !== "object" || Array.isArray(upload)) return undefined;

  if (typeof upload.url === "string" && upload.url.trim()) {
    const fromUrl = imagePathFromUrl(upload.url);
    if (fromUrl) return fromUrl;
  }

  if (typeof upload.filename === "string" && upload.filename.trim()) {
    const filename = cdnBasenameFromMediaFilename(
      sanitizeImagePath(upload.filename),
    );
    const prefix = sanitizeImagePath(
      upload.prefix || upload.folder || upload.directory || "",
    );
    return prefix ? `${prefix}/${filename}` : filename;
  }

  return undefined;
};

/**
 * Build ordered render sections from flat High Hand content (legacy JSON order).
 * @param {Record<string, unknown>} content
 * @returns {object[]}
 */
export const buildHighHandSectionsFromFlat = (content) => {
  if (!content || typeof content !== "object") return [];
  const sections = [];

  if (content.hero && typeof content.hero === "object") {
    sections.push({ type: "hero", ...content.hero });
  }
  if (content.title || content.text) {
    sections.push({
      type: "titleSection",
      title: content.title,
      text: content.text,
    });
  }
  if (content.howItWorks) {
    sections.push({ type: "howItWorks", ...content.howItWorks });
  }
  if (Array.isArray(content.textBlocks)) {
    for (const text of content.textBlocks) {
      if (typeof text === "string" && text.trim()) {
        sections.push({ type: "textBlock", text });
      }
    }
  }
  if (content.cashPrizes) {
    sections.push({
      type: "cashPrizes",
      ersKey: content.cashPrizesErsKey || "mystery-high-hand",
    });
  }
  if (content.qualifyingPeriods && content.payoutPeriods) {
    sections.push({
      type: "twoColumnPeriods",
      qualifyingPeriods: content.qualifyingPeriods,
      payoutPeriods: content.payoutPeriods,
    });
  }
  if (content.smallInfo) {
    sections.push({ type: "smallInfo", text: content.smallInfo });
  }
  if (content.elevatedTournamentLineup) {
    sections.push({
      type: "elevatedTournamentLineup",
      ...content.elevatedTournamentLineup,
    });
  }
  if (content.titan) {
    sections.push({ type: "titan", ...content.titan });
  }
  if (content.nationalPokerDay) {
    sections.push({ type: "nationalPokerDay", ...content.nationalPokerDay });
  }
  if (content.showCashGamesInfo) {
    sections.push({ type: "cashGamesInfo" });
  }
  if (content.generalRules) {
    sections.push({ type: "generalRules", ...content.generalRules });
  }

  return sections;
};

/**
 * Map one Payload layout block list to the tab content shape used by HighHandPageContent.
 * Preserves CMS drag-and-drop order in `sections`.
 * @param {unknown[]} blocks
 * @param {string} [locale]
 */
export const mapHighHandLayoutBlocks = (blocks, locale = "en") => {
  const content = {
    hero: { bgColor: "#000" },
    textBlocks: [],
    cashPrizes: false,
    cashPrizesErsKey: "mystery-high-hand",
    showCashGamesInfo: false,
    showEventFinished: true,
    sections: [],
  };

  if (!Array.isArray(blocks)) return content;

  let sawTitleSection = false;

  for (const block of blocks) {
    if (!block || typeof block !== "object") continue;
    const type = block.blockType;

    if (type === "hero") {
      const hero = {
        bgColor: typeof block.bgColor === "string" ? block.bgColor : "#000",
        image: resolveHighHandMediaPath(block.image, locale),
        bgImage: resolveHighHandMediaPath(block.bgImage, locale),
        fluidImage: Boolean(block.fluidImage),
      };
      content.sections.push({ type: "hero", ...hero });
      continue;
    }

    if (type === "titleSection") {
      const title = localizedStringForLocale(block.title, locale);
      const text = localizedStringForLocale(block.text, locale);
      const titleAs =
        typeof block.titleAs === "string" && block.titleAs.trim()
          ? block.titleAs.trim()
          : "h2";
      const highlightColor =
        typeof block.highlightColor === "string" && block.highlightColor.trim()
          ? block.highlightColor.trim()
          : undefined;
      const textFontSize =
        typeof block.textFontSize === "string" && block.textFontSize.trim()
          ? block.textFontSize.trim()
          : undefined;
      const bgColor =
        typeof block.bgColor === "string" && block.bgColor.trim()
          ? block.bgColor.trim()
          : undefined;
      if (!sawTitleSection) {
        content.title = title;
        content.text = text;
        sawTitleSection = true;
      } else {
        const parts = [title, text].filter((p) => p && p.trim()).join("\n\n");
        if (parts) content.textBlocks.push(parts);
      }
      content.sections.push({
        type: "titleSection",
        title,
        text,
        titleAs,
        highlightColor,
        textFontSize,
        bgColor,
      });
      continue;
    }

    if (type === "textContent") {
      const sectionKey =
        typeof block.sectionKey === "string" ? block.sectionKey.trim() : "";
      const body = localizedStringForLocale(block.content, locale);
      const title = localizedStringForLocale(block.title, locale);
      const linkLabel = localizedStringForLocale(block.linkLabel, locale);

      if (sectionKey === "smallInfo") {
        if (body.trim()) {
          content.smallInfo = body;
          content.sections.push({ type: "smallInfo", text: body });
        }
        continue;
      }

      if (sectionKey === "titan") {
        const titan = {
          title: title || undefined,
          text: body,
          image: resolveHighHandMediaPath(block.image, locale),
        };
        content.titan = titan;
        content.sections.push({ type: "titan", ...titan });
        continue;
      }

      if (sectionKey === "nationalPokerDay") {
        const nationalPokerDay = {
          title: title || undefined,
          text: body,
          image: resolveHighHandMediaPath(block.image, locale),
        };
        content.nationalPokerDay = nationalPokerDay;
        content.sections.push({ type: "nationalPokerDay", ...nationalPokerDay });
        continue;
      }

      if (sectionKey === "elevatedTournamentLineup") {
        const elevatedTournamentLineup = {
          title: title || undefined,
          text: body,
          link: linkLabel || undefined,
        };
        content.elevatedTournamentLineup = elevatedTournamentLineup;
        content.sections.push({
          type: "elevatedTournamentLineup",
          ...elevatedTournamentLineup,
        });
        continue;
      }

      if (sectionKey === "howItWorks") {
        const howItWorks = {
          title: title || undefined,
          text: body,
        };
        content.howItWorks = howItWorks;
        content.sections.push({ type: "howItWorks", ...howItWorks });
        continue;
      }

      if (body.trim()) {
        content.textBlocks.push(body);
        content.sections.push({ type: "textBlock", text: body });
      }
      continue;
    }

    if (type === "cashPrizesTable") {
      content.cashPrizes = true;
      const ersKey =
        typeof block.ersKey === "string" && block.ersKey.trim()
          ? block.ersKey.trim()
          : "mystery-high-hand";
      content.cashPrizesErsKey = ersKey;
      content.sections.push({ type: "cashPrizes", ersKey });
      continue;
    }

    if (type === "twoColumnPeriods") {
      const qualifyingPeriods = {
        title: localizedStringForLocale(block.leftColumnTitle, locale),
        text: localizedStringForLocale(block.leftColumnText, locale),
      };
      const payoutPeriods = {
        title: localizedStringForLocale(block.rightColumnTitle, locale),
        text: localizedStringForLocale(block.rightColumnText, locale),
      };
      content.qualifyingPeriods = qualifyingPeriods;
      content.payoutPeriods = payoutPeriods;
      content.sections.push({
        type: "twoColumnPeriods",
        qualifyingPeriods,
        payoutPeriods,
        leftColumnIcon:
          typeof block.leftColumnIcon === "string" && block.leftColumnIcon.trim()
            ? block.leftColumnIcon.trim()
            : "LuAlarmClock",
        rightColumnIcon:
          typeof block.rightColumnIcon === "string" && block.rightColumnIcon.trim()
            ? block.rightColumnIcon.trim()
            : "BiSolidDollarCircle",
      });
      continue;
    }

    if (type === "cashGamesInfo") {
      content.showCashGamesInfo = true;
      content.sections.push({ type: "cashGamesInfo" });
      continue;
    }

    if (type === "generalRules") {
      const generalRules = {
        title: localizedStringForLocale(block.title, locale),
        list: localizedStringForLocale(block.rulesList, locale),
      };
      content.generalRules = generalRules;
      content.sections.push({ type: "generalRules", ...generalRules });
    }
  }

  // Prefer first hero block from sections when present.
  const firstHero = content.sections.find((s) => s.type === "hero");
  if (firstHero) {
    content.hero = {
      bgColor: firstHero.bgColor || "#000",
      image: firstHero.image,
      bgImage: firstHero.bgImage,
      fluidImage: firstHero.fluidImage,
    };
  }

  return content;
};

/**
 * Normalize a JSON tab body so HighHandPageContent can render it like Payload-mapped content.
 * @param {Record<string, unknown>|null|undefined} content
 */
export const normalizeJsonHighHandContent = (content) => {
  if (!content || typeof content !== "object") return null;
  const normalized = {
    ...content,
    hero: content.hero || { bgColor: "#000" },
    showEventFinished: true,
    showCashGamesInfo: true,
    cashPrizes: Boolean(content.cashPrizes),
    cashPrizesErsKey: content.cashPrizesErsKey || "mystery-high-hand",
    textBlocks: Array.isArray(content.textBlocks) ? content.textBlocks : [],
  };
  normalized.sections = Array.isArray(content.sections)
    ? content.sections
    : buildHighHandSectionsFromFlat(normalized);
  return normalized;
};

/**
 * Maps a playground i18n High Hand JSON root into the HighHandPageContent page shape.
 *
 * @param {Record<string, unknown>} root - e.g. highHandApril2025.json root object
 * @param {string} promotionKey
 * @returns {{
 *   promotionKey: string,
 *   pageTitle?: string,
 *   tabs: Array<{ tabKey: string, label: string, content: object }> | null,
 *   content: object,
 * }}
 */
export function mapJsonHighHandToPage(root, promotionKey) {
  const labels =
    root?.tabs && typeof root.tabs === "object" && !Array.isArray(root.tabs)
      ? root.tabs
      : null;

  const tabKeys = labels
    ? Object.keys(labels).filter(
        (k) => root[k] && typeof root[k] === "object",
      )
    : root?.highHand
      ? ["highHand"]
      : [];

  const mappedTabs = tabKeys.map((tabKey) => ({
    tabKey,
    label: labels?.[tabKey] || tabKey,
    content: normalizeJsonHighHandContent(root[tabKey]),
  }));

  const single = normalizeJsonHighHandContent(root?.highHand) || mappedTabs[0]?.content;

  return {
    promotionKey,
    pageTitle: typeof root?.title === "string" ? root.title : undefined,
    tabs: mappedTabs.length > 1 ? mappedTabs : null,
    content: mappedTabs.length > 1 ? mappedTabs[0].content : single,
  };
}

/**
 * Maps a Payload `high-hand-promotions` document for HighHandPreview / page renderer.
 *
 * Live Preview method-override responses often keep localized fields as `{ en, fr }`
 * maps (and localized uploads as `{ en: id, fr: id }`). This mapper unwraps them.
 *
 * @param {Record<string, unknown>} doc
 * @param {string} [locale]
 * @returns {{
 *   id?: string|number,
 *   promotionKey?: string,
 *   pageTitle?: string,
 *   slug?: string,
 *   tabs: Array<{ tabKey: string, label: string, content: object }> | null,
 *   content: object,
 * }}
 */
export function mapHighHandDoc(doc, locale = "en") {
  const lang = locale === "fr" ? "fr" : "en";
  const tabsRaw = Array.isArray(doc?.tabs) ? doc.tabs : [];
  const mappedTabs = tabsRaw
    .filter((tab) => tab && typeof tab === "object" && tab.tabKey)
    .map((tab) => {
      const tabKey = String(tab.tabKey);
      const label = localizedStringForLocale(tab.label, lang);
      return {
        tabKey,
        label: label || tabKey,
        content: mapHighHandLayoutBlocks(tab.layout, lang),
      };
    });

  const rootContent = mapHighHandLayoutBlocks(doc?.layout, lang);

  return {
    id: doc?.id != null ? doc.id : undefined,
    promotionKey:
      typeof doc?.promotionKey === "string" ? doc.promotionKey : undefined,
    pageTitle: localizedStringForLocale(doc?.pageTitle, lang) || undefined,
    slug: localizedStringForLocale(doc?.slug, lang) || undefined,
    tabs: mappedTabs.length > 0 ? mappedTabs : null,
    content: mappedTabs[0]?.content || rootContent,
  };
}

/**
 * True when mapped High Hand content has enough to render (not an empty shell).
 * Empty Payload layouts still produce a content object — do not treat that as success.
 * @param {object|null|undefined} content
 * @returns {boolean}
 */
export function isMeaningfulHighHandContent(content) {
  if (!content || typeof content !== "object") return false;
  const hero = content.hero || {};
  if (hero.image || hero.bgImage) return true;
  if (content.title || content.text) return true;
  if (content.howItWorks?.title || content.howItWorks?.text) return true;
  if (content.generalRules?.title || content.generalRules?.list) return true;
  if (content.smallInfo) return true;
  if (content.titan?.text || content.nationalPokerDay?.text) return true;
  if (content.elevatedTournamentLineup?.text) return true;
  if (content.qualifyingPeriods?.text || content.payoutPeriods?.text) return true;
  if (
    Array.isArray(content.textBlocks) &&
    content.textBlocks.some((block) => typeof block === "string" && block.trim())
  ) {
    return true;
  }
  if (content.cashPrizes) return true;
  return false;
}

/**
 * @param {object|null|undefined} page - mapHighHandDoc / mapJsonHighHandToPage result
 * @returns {boolean}
 */
export function isMeaningfulHighHandPage(page) {
  if (!page || typeof page !== "object") return false;
  if (!page.promotionKey) return false;
  if (Array.isArray(page.tabs) && page.tabs.length > 0) {
    return page.tabs.some((tab) => isMeaningfulHighHandContent(tab?.content));
  }
  return isMeaningfulHighHandContent(page.content);
}
